// UI-3: the one foundation every v2 overlay stands on (Modal, ConfirmDialog, SidePanel, Sheet,
// Popover, Menu, RichDropdown, MoveToSheet). mobile-responsive-addendum.md 2b and 6.
//
//   Layer         a portal to <body>, so no transformed card or clipped list can trap an overlay
//   registry      how many overlays are open, for FloatingAsk (hidden while any is open) and tests
//   useScrollLock the page behind does not scroll while an overlay is open (iOS too), ref-counted
//   useFocusTrap  focus moves in, Tab stays in, Escape closes, focus returns to what opened it
//   useKeyboardViewport  on a phone, the overlay fits the space ABOVE the iOS keyboard, and the
//                 focused field is scrolled into view, so inputs are never hidden behind it
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './v2.css';

// ── The registry ────────────────────────────────────────────────────────────────────────────
let openCount = 0;
const listeners = new Set();
const emit = () => {
  if (typeof document !== 'undefined') {
    if (openCount > 0) document.documentElement.setAttribute('data-ui-overlay-open', String(openCount));
    else document.documentElement.removeAttribute('data-ui-overlay-open');
  }
  listeners.forEach((fn) => fn(openCount));
};
export function openOverlayCount() { return openCount; }
export function subscribeOverlays(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Counts this overlay as open while `open` is true.
export function useOverlayRegistration(open) {
  useEffect(() => {
    if (!open) return undefined;
    openCount += 1; emit();
    return () => { openCount -= 1; emit(); };
  }, [open]);
}

// True while any v2 overlay is open (FloatingAsk reads it).
export function useAnyOverlayOpen() {
  const [n, setN] = useState(openCount);
  useEffect(() => subscribeOverlays(setN), []);
  return n > 0;
}

// ── Scroll lock ─────────────────────────────────────────────────────────────────────────────
// position: fixed on <body> is what stops iOS Safari scrolling the page behind; the scroll
// position is kept and put back. Ref-counted, so a sheet over a modal unlocks only at the end.
let locks = 0;
let saved = null;
export function useScrollLock(open) {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    if (locks === 0) {
      const { body } = document;
      saved = { y: window.scrollY, style: body.getAttribute('style') };
      body.style.position = 'fixed';
      body.style.top = `-${saved.y}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.overflow = 'hidden';
    }
    locks += 1;
    return () => {
      locks -= 1;
      if (locks === 0 && saved) {
        const { body } = document;
        if (saved.style === null) body.removeAttribute('style'); else body.setAttribute('style', saved.style);
        if (window.scrollY !== saved.y) window.scrollTo(0, saved.y);
        saved = null;
      }
    };
  }, [open]);
}

// ── Focus trap ──────────────────────────────────────────────────────────────────────────────
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
export function focusables(root) {
  return root ? [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[inert]') && el.getAttribute('aria-hidden') !== 'true') : [];
}

// The control a person last pressed. Safari does not focus a button when it is tapped, so on an
// iPhone document.activeElement is still <body> when an overlay opens; returning focus to <body>
// would leave VoiceOver at the top of the page. The pressed control is where focus goes back.
let lastPressed = null;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => {
    lastPressed = e.target?.closest?.('button, a[href], [role="button"], input, select, textarea, [tabindex]') || null;
  }, true);
}

// Focus moves into `ref` on open (initialFocus, else the first field, else the panel); Tab and
// Shift+Tab wrap inside it; Escape calls onEscape; on close focus returns to whatever had it.
export function useFocusTrap(ref, open, { onEscape, initialFocus } = {}) {
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    if (!open) return undefined;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    // Safari also moves focus to a focusable panel round the pressed button (a tab panel has
    // tabIndex 0), so a focused element that holds the last pressed control gives way to it.
    const pressedInside = lastPressed && active && active !== lastPressed && active.contains(lastPressed);
    const before = active && active !== document.body && !pressedInside ? active : lastPressed;
    const root = ref.current;
    const first = initialFocus?.current || root?.querySelector('[data-autofocus]')
      || focusables(root).find((el) => el.matches('input, select, textarea')) || focusables(root)[0] || root;
    first?.focus?.({ preventScroll: true });
    const onKey = (e) => {
      if (!ref.current) return;
      // Only the topmost overlay answers (a sheet opened over a modal).
      const layers = document.querySelectorAll('[data-ui-layer]');
      if (layers.length && layers[layers.length - 1] !== ref.current.closest('[data-ui-layer]')) return;
      if (e.key === 'Escape') { e.stopPropagation(); escape.current?.(); return; }
      if (e.key !== 'Tab') return;
      const list = focusables(ref.current);
      if (!list.length) { e.preventDefault(); return; }
      const at = list.indexOf(document.activeElement);
      if (e.shiftKey && (at <= 0)) { e.preventDefault(); list[list.length - 1].focus(); }
      else if (!e.shiftKey && (at === list.length - 1 || at === -1)) { e.preventDefault(); list[0].focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Back to the trigger, if it is still on the page.
      if (before && typeof before.focus === 'function' && document.contains(before)) before.focus({ preventScroll: true });
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── The iOS keyboard ────────────────────────────────────────────────────────────────────────
// iOS shows the keyboard OVER the layout viewport: 100dvh does not shrink, and a footer pinned
// to the bottom ends up under the keys. The visual viewport does shrink, so a phone overlay
// sizes itself to it (--ui-vv-height, --ui-vv-top) and the focused field is scrolled into view.
export function useKeyboardViewport(ref, open) {
  useLayoutEffect(() => {
    const el = ref.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!open || !el) return undefined;
    const fit = () => {
      if (!vv) return;
      el.style.setProperty('--ui-vv-height', `${Math.round(vv.height)}px`);
      el.style.setProperty('--ui-vv-top', `${Math.round(vv.offsetTop)}px`);
      // How far the visual viewport's bottom sits above the layout viewport's (the keyboard).
      el.style.setProperty('--ui-vv-bottom', `${Math.max(0, Math.round(window.innerHeight - vv.offsetTop - vv.height))}px`);
    };
    const onFocus = (e) => {
      if (!e.target.matches?.('input, textarea, select')) return;
      setTimeout(() => e.target.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 250);
    };
    fit();
    vv?.addEventListener('resize', fit);
    vv?.addEventListener('scroll', fit);
    el.addEventListener('focusin', onFocus);
    return () => {
      vv?.removeEventListener('resize', fit);
      vv?.removeEventListener('scroll', fit);
      el.removeEventListener('focusin', onFocus);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// A portal to <body>, marked so the focus trap knows which overlay is on top.
export function Layer({ children }) {
  if (typeof document === 'undefined') return null;
  return createPortal(<div data-ui-layer="">{children}</div>, document.body);
}

// Everything a full overlay needs, in one call.
export function useOverlay(ref, open, { onClose, initialFocus, keyboard = true } = {}) {
  useOverlayRegistration(open);
  useScrollLock(open);
  useFocusTrap(ref, open, { onEscape: onClose, initialFocus });
  useKeyboardViewport(ref, open && keyboard);
}

// The header band's close button, used by Modal, SidePanel and Sheet.
export function CloseButton({ onClick, label = 'Close' }) {
  return (
    <button type="button" className="ui-overlay__close ui-hit" aria-label={label} onClick={onClick}>
      <span aria-hidden="true">✕</span>
    </button>
  );
}
